import { motion } from 'motion/react';
import { ArrowRight, Clock, Users, Zap, Brain, Mail, MessageSquare, CheckCircle, Star, Building2, Globe, TrendingUp, Copy } from 'lucide-react';
import { useState } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { AuthModal } from '../components/AuthModal';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { trackPlanSelected } from '@/lib/analytics';

export function ForRecruitersPage() {
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const handleGetStarted = () => {
    trackPlanSelected('free', 'monthly', 0);
    if (isAuthenticated) {
      navigate('/account');
    } else {
      setIsAuthModalOpen(true);
    }
  };

  const handleViewPricing = () => {
    navigate('/pricing');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1a0a2e] via-[#16213e] to-[#0F2847]">
      <Header />

      <main>
        {/* Hero Section */}
        <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-block px-4 py-2 bg-violet-500/20 text-violet-400 rounded-full text-sm font-semibold mb-6 border border-violet-500/30">
                For Recruitment Agencies & Staffing Firms
              </span>
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
                Place More Candidates,<br />
                <span className="text-violet-400">Write Better Outreach</span>
              </h1>
              <p className="text-xl sm:text-2xl text-white/80 max-w-3xl mx-auto mb-4">
                Your recruiters send hundreds of emails daily to US hiring managers and candidates.
                xReplAI ensures every message is polished, professional, and gets responses.
              </p>
              <p className="text-lg text-white/60 max-w-2xl mx-auto mb-10">
                AI-powered email drafts that sound native, professional, and personalized -- at the speed and scale your agency needs.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={handleGetStarted}
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-violet-500 text-white font-semibold rounded-lg hover:bg-violet-500/90 transition-all shadow-lg shadow-violet-500/30 text-lg"
                >
                  Try Free -- 30 Drafts/Month
                  <ArrowRight size={20} />
                </button>
                <button
                  onClick={handleViewPricing}
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 text-white font-semibold rounded-lg hover:bg-white/20 transition-all border border-white/20 text-lg"
                >
                  See Team Pricing
                </button>
              </div>
              <p className="text-sm text-white/50 mt-4">
                No credit card required. Works instantly in Gmail and LinkedIn.
              </p>
            </motion.div>
          </div>
        </section>

        {/* The Agency Problem Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white/5 backdrop-blur-sm">
          <div className="max-w-6xl mx-auto">
            <motion.div
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                The Recruitment Agency Email Challenge
              </h2>
              <p className="text-lg text-white/70 max-w-2xl mx-auto">
                Your team is talented at finding matches. But email communication with US clients can be a bottleneck.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  icon: Globe,
                  title: 'Cross-cultural communication gaps',
                  description: 'Your team writes great emails in their native language. But US hiring managers expect specific phrasing, idioms, and formality levels that are hard to nail consistently.',
                  stat: '67%',
                  statLabel: 'of US hiring managers judge agencies by email quality',
                },
                {
                  icon: Clock,
                  title: 'Speed vs. quality trade-off',
                  description: 'Recruiters need to send 50-100+ personalized emails daily. Spending 10 minutes per email to get the English right means fewer placements.',
                  stat: '3hrs',
                  statLabel: 'per day spent just on email composition',
                },
                {
                  icon: Copy,
                  title: 'Templates sound like templates',
                  description: 'US hiring managers and candidates can spot a template instantly. Generic outreach gets ignored. Personalization takes time your team doesn\'t have.',
                  stat: '14%',
                  statLabel: 'average reply rate for templated recruiter emails',
                },
              ].map((pain, i) => (
                <motion.div
                  key={i}
                  className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                >
                  <pain.icon size={32} className="text-red-400 mb-4" />
                  <h3 className="text-xl font-bold text-white mb-3">{pain.title}</h3>
                  <p className="text-white/70 mb-6">{pain.description}</p>
                  <div className="border-t border-white/10 pt-4">
                    <span className="text-3xl font-bold text-violet-400">{pain.stat}</span>
                    <p className="text-xs text-white/50 mt-1">{pain.statLabel}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Solution Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <motion.div
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Scale Your Outreach Without Sacrificing Quality
              </h2>
              <p className="text-lg text-white/70 max-w-2xl mx-auto">
                Give every recruiter on your team the writing ability of a native English professional
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8">
              {[
                {
                  icon: Zap,
                  title: 'Instant Professional Drafts',
                  description: 'Each recruiter clicks one button and gets 2-4 polished draft options for any email -- candidate outreach, client updates, interview scheduling, offer discussions. All in perfect English.',
                  highlight: '30 seconds per email',
                },
                {
                  icon: Brain,
                  title: 'Context-Aware Intelligence',
                  description: 'xReplAI reads the full email thread and understands if it\'s an initial outreach, a follow-up, a scheduling email, or a negotiation. Each draft is tailored to the exact situation.',
                  highlight: 'Smart context detection',
                },
                {
                  icon: Users,
                  title: 'Consistent Agency Voice',
                  description: 'Style matching ensures all your recruiters sound equally professional, regardless of their individual English proficiency. Your agency\'s reputation stays consistent across every interaction.',
                  highlight: 'Brand consistency at scale',
                },
                {
                  icon: MessageSquare,
                  title: 'Gmail + LinkedIn in One Tool',
                  description: 'Whether your team sources on LinkedIn InMail or communicates via Gmail, xReplAI works in both. One Chrome extension covers both primary recruitment channels.',
                  highlight: 'Works where recruiters work',
                },
              ].map((feature, i) => (
                <motion.div
                  key={i}
                  className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-violet-500/20 flex items-center justify-center">
                      <feature.icon size={24} className="text-violet-400" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
                      <p className="text-white/70 mb-3">{feature.description}</p>
                      <span className="inline-block px-3 py-1 bg-violet-500/10 text-violet-400 rounded-full text-sm font-medium border border-violet-500/20">
                        {feature.highlight}
                      </span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Use Cases for Agencies */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white/5 backdrop-blur-sm">
          <div className="max-w-5xl mx-auto">
            <motion.div
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Built for Every Recruitment Email
              </h2>
              <p className="text-lg text-white/70 max-w-2xl mx-auto">
                From sourcing to placement, xReplAI handles the full recruitment communication lifecycle
              </p>
            </motion.div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { title: 'Candidate sourcing', description: 'Professional first-touch outreach on LinkedIn that stands out from spam' },
                { title: 'Client proposals', description: 'Pitch candidates to US hiring managers with polished, persuasive descriptions' },
                { title: 'Interview coordination', description: 'Schedule and confirm interviews with clear, professional communication' },
                { title: 'Offer negotiations', description: 'Navigate sensitive compensation discussions with the right tone' },
                { title: 'Follow-up sequences', description: 'Persistent but professional follow-ups that don\'t feel pushy' },
                { title: 'Client relationship emails', description: 'Maintain agency reputation with consistently excellent communication' },
              ].map((useCase, i) => (
                <motion.div
                  key={i}
                  className="bg-white/5 rounded-xl p-6 border border-white/10"
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                >
                  <Building2 size={20} className="text-violet-400 mb-3" />
                  <h3 className="text-lg font-semibold text-white mb-2">{useCase.title}</h3>
                  <p className="text-sm text-white/60">{useCase.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ROI Calculator / Impact Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <motion.div
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                The ROI Is Immediate
              </h2>
              <p className="text-lg text-white/70 max-w-2xl mx-auto">
                A $30/month tool that saves each recruiter 2-3 hours per day
              </p>
            </motion.div>

            <div className="grid md:grid-cols-4 gap-6">
              {[
                {
                  metric: '80%',
                  label: 'Less time writing emails',
                  description: '2-3 hours saved per recruiter per day',
                },
                {
                  metric: '3x',
                  label: 'More outreach volume',
                  description: 'Same team, triple the candidate touchpoints',
                },
                {
                  metric: '2x',
                  label: 'Higher response rates',
                  description: 'Personalized, native English beats templates',
                },
                {
                  metric: '$30',
                  label: 'Per recruiter/month',
                  description: 'Pays for itself with one extra placement/year',
                },
              ].map((stat, i) => (
                <motion.div
                  key={i}
                  className="text-center p-6 bg-white/5 rounded-2xl border border-white/10"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                >
                  <div className="text-3xl font-bold text-violet-400 mb-2">{stat.metric}</div>
                  <div className="text-base font-semibold text-white mb-2">{stat.label}</div>
                  <div className="text-xs text-white/60">{stat.description}</div>
                </motion.div>
              ))}
            </div>

            {/* Testimonial */}
            <motion.div
              className="mt-12 bg-gradient-to-br from-white/10 to-white/5 rounded-2xl p-8 border border-white/10 text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <Star size={32} className="text-yellow-400 mx-auto mb-4" />
              <blockquote className="text-lg text-white/90 italic mb-4 max-w-2xl mx-auto">
                "Our agency placed 15 recruiters on xReplAI Pro. The difference in our client response rates was noticeable within the first week. Our US clients started commenting that our communication quality improved. That's worth far more than $30/seat."
              </blockquote>
              <p className="text-white/60 text-sm">
                -- Operations Director, IT Staffing Agency
              </p>
            </motion.div>
          </div>
        </section>

        {/* Pricing Teaser */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white/5 backdrop-blur-sm">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Start With One Recruiter, Scale to Your Team
              </h2>
              <p className="text-lg text-white/70 mb-10 max-w-2xl mx-auto">
                Try free with one account. When you see the results, roll it out across your team.
              </p>

              <div className="grid sm:grid-cols-2 gap-6 max-w-2xl mx-auto mb-10">
                <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
                  <h3 className="text-xl font-bold text-white mb-2">Free Trial</h3>
                  <div className="text-3xl font-bold text-white mb-1">$0</div>
                  <p className="text-sm text-white/60 mb-4">per recruiter, forever</p>
                  <ul className="text-left text-sm text-white/70 space-y-2">
                    <li className="flex items-center gap-2"><CheckCircle size={16} className="text-green-400 flex-shrink-0" /> 30 drafts/month</li>
                    <li className="flex items-center gap-2"><CheckCircle size={16} className="text-green-400 flex-shrink-0" /> Gmail + LinkedIn</li>
                    <li className="flex items-center gap-2"><CheckCircle size={16} className="text-green-400 flex-shrink-0" /> 2 draft variants</li>
                    <li className="flex items-center gap-2"><CheckCircle size={16} className="text-green-400 flex-shrink-0" /> Instant setup</li>
                  </ul>
                </div>
                <div className="bg-gradient-to-br from-violet-900/50 to-violet-600/30 rounded-2xl p-6 border border-violet-500/50 relative">
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-violet-500 text-white rounded-full text-xs font-semibold">
                    Recommended for Teams
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">Pro</h3>
                  <div className="text-3xl font-bold text-white mb-1">$30<span className="text-lg font-normal">/mo</span></div>
                  <p className="text-sm text-white/70 mb-4">per recruiter ($252/yr, save 30%)</p>
                  <ul className="text-left text-sm text-white/90 space-y-2">
                    <li className="flex items-center gap-2"><CheckCircle size={16} className="text-green-300 flex-shrink-0" /> 500 drafts/month</li>
                    <li className="flex items-center gap-2"><CheckCircle size={16} className="text-green-300 flex-shrink-0" /> Style matching</li>
                    <li className="flex items-center gap-2"><CheckCircle size={16} className="text-green-300 flex-shrink-0" /> 4 draft variants</li>
                    <li className="flex items-center gap-2"><CheckCircle size={16} className="text-green-300 flex-shrink-0" /> GPT-4o + Claude + Grok</li>
                  </ul>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={handleGetStarted}
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-violet-500 text-white font-semibold rounded-lg hover:bg-violet-500/90 transition-all shadow-lg shadow-violet-500/30"
                >
                  Start Free Trial
                  <ArrowRight size={20} />
                </button>
                <button
                  onClick={handleViewPricing}
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 text-white font-semibold rounded-lg hover:bg-white/20 transition-all border border-white/20"
                >
                  Compare All Plans
                </button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Your Recruiters Find the Talent.<br />
                <span className="text-violet-400">Let xReplAI Handle the Emails.</span>
              </h2>
              <p className="text-lg text-white/70 mb-8 max-w-xl mx-auto">
                Professional English emails for every recruiter on your team. Install in 2 minutes, see results immediately.
              </p>
              <button
                onClick={handleGetStarted}
                className="inline-flex items-center justify-center gap-2 px-10 py-5 bg-violet-500 text-white font-bold rounded-lg hover:bg-violet-500/90 transition-all shadow-lg shadow-violet-500/30 text-lg"
              >
                Get Started Free
                <ArrowRight size={22} />
              </button>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
      <AuthModal isOpen={isAuthModalOpen} onClose={() => setIsAuthModalOpen(false)} />
    </div>
  );
}
